import React, { forwardRef } from 'react'
import { constants } from 'common/helpers'
import { useMediaTypes } from 'sb-hooks'
import cx from 'classnames'

import Cell from './Cell/Cell'
import LastFlexibleCell from './LastFlexibleCell/LastFlexibleCell'

import type { GridCellProps } from './Cell/Cell'
import type { LastFlexibleGridCellProps } from './LastFlexibleCell/LastFlexibleCell'

import s from './Grid.scss'


const allowedGutterSizes = [ 8, 12, 16, 24, 32, 48 ] as const
const allowedColumnsSizes = [ 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12 ] as const
const mediaKeys = [ ...constants.mediaList ] as const

type MediaKey = typeof mediaKeys[number]
type GutterSize = typeof allowedGutterSizes[number]
type MediaGutterType = { [ key in MediaKey ]?: GutterSize }

export type GridMediaColumnsSize = { [ key in MediaKey ]?: GridColumnsSize }
export type GridColumnsSize = typeof allowedColumnsSizes[number]

export type GridProps = {
  children: React.ReactNode
  id?: string
  className?: string
  columns: GridColumnsSize | GridMediaColumnsSize
  gutter?: GutterSize | MediaGutterType
  alignItems?: 'center' | 'stretch'
  noWrap?: boolean
  dataTestId?: string
}

export interface CompoundedComponent extends React.ForwardRefExoticComponent<GridProps & React.RefAttributes<HTMLDivElement>> {
  Cell: React.FunctionComponent<GridCellProps>
  LastFlexibleCell: React.FunctionComponent<LastFlexibleGridCellProps>
}

const Grid = forwardRef<HTMLDivElement, GridProps>((props, ref) => {
  let { children, id, className, columns = 1, gutter, alignItems, noWrap, dataTestId } = props

  const mediaTypes = useMediaTypes()

  if (typeof columns === 'object') {
    const mediaType = Object.keys(columns)
      .find((key) => mediaTypes[key])

    if (!mediaType) {
      console.warn(`No active media type found`)
      return null
    }

    columns = columns[mediaType]
    gutter = gutter && typeof gutter === 'object' ? gutter[mediaType] : gutter
  }

  const gridClassName = cx(s.grid, className, {
    [s['alignItems-center']]: alignItems === 'center',
    [s['alignItems-stretch']]: alignItems === 'stretch',
    [s[`gutter_${gutter}`]]: gutter,
    [s.noWrap]: noWrap,
  })

  return (
    <div
      ref={ref}
      id={id}
      className={gridClassName}
      role="list"
      data-testid={dataTestId}
    >
      {
        React.Children.map<any, React.ReactNode>(children, (child, index) => {
          if (child) {
            const cellProps = { columns, gutter } as { columns: GridColumnsSize, gutter: GridProps['gutter'] }

            if (React.isValidElement(child)) {
              if (child.type === LastFlexibleCell) {
                return React.cloneElement(child as React.ReactElement<LastFlexibleGridCellProps>, { ...cellProps, index })
              }
              if (child.type === Cell) {
                return React.cloneElement(child, cellProps)
              }
            }

            return (
              <Grid.Cell {...cellProps}>
                {child}
              </Grid.Cell>
            )
          }
        })
      }
    </div>
  )
}) as CompoundedComponent

Grid.Cell = Cell
Grid.LastFlexibleCell = LastFlexibleCell


export default Grid
